import { useEffect, useState } from 'react'

const loadingImage = `${process.env.NEXT_PUBLIC_ASSETS_PATH}/default-loader.gif`
const failedImageCacheTtlMs = 10_000

const loadedImageCache = new Map<string, true>()
const failedImageCache = new Map<string, number>()

const hasRecentFailure = (targetSrc: string) => {
  const failedAt = failedImageCache.get(targetSrc)

  if (failedAt === undefined) return false
  if (Date.now() - failedAt > failedImageCacheTtlMs) {
    failedImageCache.delete(targetSrc)
    return false
  }

  return true
}

const getInitialSrc = (targetSrc: string, defaultSrc: string) => {
  if (!targetSrc) return defaultSrc
  if (loadedImageCache.has(targetSrc)) return targetSrc
  if (hasRecentFailure(targetSrc)) return defaultSrc
  return loadingImage
}

const getInitialSrcWithFallback = (
  targetSrc: string,
  defaultSrc: string,
  fallbackTargetSrc?: string,
) => {
  if (fallbackTargetSrc && loadedImageCache.has(fallbackTargetSrc)) {
    return fallbackTargetSrc
  }

  return getInitialSrc(targetSrc, defaultSrc)
}

export const __clearPreloadImageCacheForTests = () => {
  loadedImageCache.clear()
  failedImageCache.clear()
}

export const usePreloadImage = (
  targetSrc: string,
  defaultSrc: string,
  maxRetries: number = 5,
  baseDelay: number = 500,
  fallbackTargetSrc?: string,
) => {
  const [currentSrc, setCurrentSrc] = useState(() =>
    getInitialSrcWithFallback(targetSrc, defaultSrc, fallbackTargetSrc),
  )

  useEffect(() => {
    if (!targetSrc) {
      setCurrentSrc(defaultSrc)
      return
    }

    if (loadedImageCache.has(targetSrc)) {
      setCurrentSrc(targetSrc)
      return
    }

    const failedRecently = hasRecentFailure(targetSrc)
    const fallbackLoaded =
      typeof fallbackTargetSrc === 'string' &&
      loadedImageCache.has(fallbackTargetSrc)

    setCurrentSrc((previousSrc) => {
      if (previousSrc === targetSrc) return targetSrc
      if (previousSrc && previousSrc !== loadingImage) return previousSrc
      if (fallbackLoaded) return fallbackTargetSrc
      return failedRecently ? defaultSrc : loadingImage
    })

    let retries = 0
    let isActive = true
    let retryTimeout: ReturnType<typeof setTimeout> | undefined
    let currentImage: HTMLImageElement | null = null
    let activeTargetSrc = targetSrc
    let usingFallbackTarget = false

    const clearCurrentImage = () => {
      if (!currentImage) return
      currentImage.onload = null
      currentImage.onerror = null
      currentImage = null
    }

    const switchToFallbackTarget = () => {
      if (
        !fallbackTargetSrc ||
        usingFallbackTarget ||
        fallbackTargetSrc === activeTargetSrc
      ) {
        return false
      }

      failedImageCache.set(activeTargetSrc, Date.now())
      usingFallbackTarget = true
      activeTargetSrc = fallbackTargetSrc
      retries = 0
      return true
    }

    const tryLoad = () => {
      if (!isActive) return

      clearCurrentImage()

      const img = new Image()
      currentImage = img
      img.src = activeTargetSrc
      img.decoding = 'async'
      img.onload = () => {
        if (!isActive) return
        loadedImageCache.set(activeTargetSrc, true)
        failedImageCache.delete(activeTargetSrc)
        setCurrentSrc(activeTargetSrc)
      }
      img.onerror = () => {
        if (!isActive) return

        retries++
        if (retries <= maxRetries) {
          retryTimeout = setTimeout(tryLoad, baseDelay * retries)
        } else if (switchToFallbackTarget()) {
          retryTimeout = setTimeout(tryLoad, 0)
        } else {
          failedImageCache.set(activeTargetSrc, Date.now())
          setCurrentSrc(defaultSrc)
        }
      }
    }

    tryLoad()

    return () => {
      isActive = false
      clearCurrentImage()
      if (retryTimeout) clearTimeout(retryTimeout)
    }
  }, [targetSrc, defaultSrc, maxRetries, baseDelay, fallbackTargetSrc])

  return currentSrc
}
